/* Typed wrappers for the Agent Proposals review inbox ("Turn On The Agents").
 *
 *   GET  /tools/proposals            → Proposal[]   (the review queue)
 *   POST /tools/proposals/advance    → approve | dismiss | edit | act
 *
 * These are SPA-direct calls (APIM injects X-User-OID). They are DELIBERATELY
 * NOT agent tools — advancing a proposal past 'pending' is a HUMAN action, so
 * the backend keeps these routes off the Foundry tool registry entirely. This
 * client mirrors the notifications.ts grammar (self-contained callTool helper).
 *
 * Vocabulary types live in schemas.ts (lockstep guard). */

import { ApiError } from './client';
import type { Proposal, ProposalKind, ProposalQueryStatus, ToolResponse } from './schemas';

export type { Proposal, ProposalKind, ProposalQueryStatus, ProposalStatus } from './schemas';

const APIM = import.meta.env.VITE_APIM_BASE_URL || 'http://localhost:7071';

async function callTool<T>(
  path: string,
  token: string | null,
  opts: { method?: 'GET' | 'POST'; body?: unknown } = {}
): Promise<ToolResponse<T>> {
  const method = opts.method ?? 'GET';
  const res = await fetch(`${APIM}${path}`, {
    method,
    headers: {
      ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
      ...(token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: method === 'GET' || opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const err = await res.json();
      msg = err?.error?.message ?? err?.detail ?? msg;
      code = err?.error?.error_code ?? err?.error_code ?? err?.code ?? undefined;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status, code);
  }
  return (await res.json()) as ToolResponse<T>;
}

/** Throw when the tools-service returns success:false at HTTP 200 so the SPA's
 *  optimistic removal doesn't stick over a real backend failure. */
function throwIfFailed(
  wrap: { success: boolean; error?: string | null; error_code?: string | null },
  verb: string
): void {
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? `Could not ${verb} the proposal`, 200, wrap.error_code ?? undefined);
  }
}

/** The review queue the caller is authorized to act on. Defaults to the open
 *  ('pending') proposals across every kind the user can see. */
export async function listProposals(
  token: string | null,
  params: { kind?: ProposalKind; status?: ProposalQueryStatus; project_id?: number; limit?: number } = {}
): Promise<Proposal[]> {
  const qs = new URLSearchParams();
  if (params.kind) qs.set('kind', params.kind);
  qs.set('status', params.status ?? 'pending');
  if (params.project_id != null) qs.set('project_id', String(params.project_id));
  if (params.limit != null) qs.set('limit', String(params.limit));
  const wrap = await callTool<Proposal>(`/tools/proposals?${qs.toString()}`, token);
  if (!wrap.success) {
    throw new ApiError(wrap.error ?? 'Failed to load proposals', 200, wrap.error_code ?? undefined);
  }
  return wrap.data ?? [];
}

/** The human review action. `action` maps to the lifecycle transition; a
 *  dismissal requires a `resolution_reason`, an edit requires `edited_content`. */
export async function advanceProposal(
  token: string | null,
  params: {
    kind: ProposalKind;
    proposal_id: number;
    action: 'approve' | 'dismiss' | 'edit' | 'act';
    resolution_reason?: 'wrong' | 'duplicate' | 'not_needed' | 'too_late' | 'other';
    resolution_note?: string;
    edited_content?: string;
  }
): Promise<void> {
  const wrap = await callTool('/tools/proposals/advance', token, { method: 'POST', body: params });
  throwIfFailed(wrap, params.action);
}

/** Snooze a pending proposal until `snooze_until` (ISO-8601). It leaves the queue
 *  and reappears once that time passes (no wake job). Pass `snooze_until: null`
 *  to un-snooze. Only a pending proposal can be snoozed (409 otherwise). */
export async function snoozeProposal(
  token: string | null,
  params: { kind: ProposalKind; proposal_id: number; snooze_until: string | null }
): Promise<void> {
  const wrap = await callTool('/tools/proposals/snooze', token, { method: 'POST', body: params });
  throwIfFailed(wrap, 'snooze');
}

/** Send a pending proposal back to its drafting agent with a note. The proposal
 *  stays open (the note is captured for the agent's next run); this does not
 *  trigger an immediate re-draft. */
export async function requestChanges(
  token: string | null,
  params: { kind: ProposalKind; proposal_id: number; note: string }
): Promise<void> {
  const wrap = await callTool('/tools/proposals/request-changes', token, { method: 'POST', body: params });
  throwIfFailed(wrap, 'request changes on');
}

/** Restore an approved/dismissed proposal to pending (undo a decision). 409 if a
 *  newer open proposal already exists for the same source, or if the item is
 *  'acted' (terminal). */
export async function restoreProposal(
  token: string | null,
  params: { kind: ProposalKind; proposal_id: number }
): Promise<void> {
  const wrap = await callTool('/tools/proposals/restore', token, { method: 'POST', body: params });
  throwIfFailed(wrap, 'restore');
}

/** Record that the reviewer opened a proposal — the ball-in-court turnaround
 *  clock START (backend stamps first_viewed_at + an 'entered_review' custody
 *  event). Idempotent and BEST-EFFORT: the SPA fires it when a card is expanded
 *  and never blocks the UI on it, so a failure is swallowed by the caller. */
export async function viewProposal(
  token: string | null,
  params: { kind: ProposalKind; proposal_id: number }
): Promise<void> {
  await callTool('/tools/proposals/view', token, { method: 'POST', body: params });
}

/** Pending-count for the bell badge. Best-effort: callers treat a failure as 0.
 *  Uses the list envelope's `count` (capped by limit — fine for a badge). */
export async function getPendingProposalsCount(token: string | null): Promise<number> {
  const wrap = await callTool<Proposal>('/tools/proposals?status=pending&limit=100', token);
  return wrap.success ? wrap.count ?? (wrap.data?.length ?? 0) : 0;
}
